import camelcaseKeys from 'camelcase-keys';
import SocialCommentsAPI from 'dashboard/api/socialComments';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const createInitialUIFlags = () => ({
  fetchingList: false,
  fetchingThread: false,
  replying: false,
  sendingPrivateReply: false,
  hiding: false,
  unhiding: false,
  liking: false,
  unliking: false,
  handling: false,
  deletingItem: false,
});

const camelizeComment = data => camelcaseKeys(data || {}, { deep: true });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || 0),
  page: Number(meta?.current_page || meta?.page || 1),
});

export const useSocialCommentsStore = createStore({
  name: 'socialComments',
  type: 'pinia',
  API: SocialCommentsAPI,
  state: () => ({
    uiFlags: createInitialUIFlags(),
    // Kept entirely separate from `records`/`meta`: those back the queue list, and a
    // thread fetch for the detail pane must never clobber the filter the agent is
    // currently looking at.
    threadRecords: [],
  }),

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    upsertRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;

      // The detail panes render `threadRecords`, which is deliberately a separate
      // collection from the queue. Writing the result of a hide, like or reply only
      // into `records` left the open thread showing pre-action state until the page
      // was reloaded. Never push here: the thread is scoped to one post, so a comment
      // it does not already hold belongs to a different post.
      const threadIndex = this.threadRecords.findIndex(r => r.id === record.id);
      if (threadIndex !== -1) this.threadRecords[threadIndex] = record;
    },

    // Unlike upsertRecord, this does add rows the thread has not seen: it is only ever
    // handed a reply the page just made to a comment already in this thread. Kept out of
    // `records`, which is the needs-reply queue and never holds the page's own replies.
    upsertThreadRecord(record) {
      const index = this.threadRecords.findIndex(r => r.id === record.id);
      if (index === -1) this.threadRecords.push(record);
      else this.threadRecords[index] = record;
    },

    clearThread() {
      this.threadRecords = [];
    },

    // Fetches the full thread for a post (including the page's own replies, which the
    // default `needs_reply` queue filter excludes) without touching `records`/`meta`.
    async fetchThread(socialPostId) {
      this.setUIFlag({ fetchingThread: true });
      try {
        const {
          data: { payload },
        } = await SocialCommentsAPI.get({ socialPostId, state: 'all' });
        this.threadRecords = camelizeComment(payload);
        return this.threadRecords;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingThread: false });
      }
    },

    async get({ inboxId, socialPostId, state, page = 1 } = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await SocialCommentsAPI.get({ inboxId, socialPostId, state, page });
        this.records = camelizeComment(payload);
        this.setMeta(meta);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async reply({ id, message }) {
      this.setUIFlag({ replying: true });
      try {
        const { data } = await SocialCommentsAPI.reply(id, message);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        // The reply itself, which the API now returns alongside the parent it answered.
        // Without this the thread had nothing to show until Meta's webhook delivered the
        // row, which took several seconds and read as a reply that had not been sent.
        if (data.created_reply) {
          this.upsertThreadRecord(camelizeComment(data.created_reply));
        }
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ replying: false });
      }
    },

    async privateReply({ id, message }) {
      this.setUIFlag({ sendingPrivateReply: true });
      try {
        const { data } = await SocialCommentsAPI.privateReply(id, message);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ sendingPrivateReply: false });
      }
    },

    async hide(id) {
      this.setUIFlag({ hiding: true });
      try {
        const { data } = await SocialCommentsAPI.hide(id);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ hiding: false });
      }
    },

    async unhide(id) {
      this.setUIFlag({ unhiding: true });
      try {
        const { data } = await SocialCommentsAPI.unhide(id);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ unhiding: false });
      }
    },

    async like(id) {
      this.setUIFlag({ liking: true });
      try {
        const { data } = await SocialCommentsAPI.like(id);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ liking: false });
      }
    },

    async unlike(id) {
      this.setUIFlag({ unliking: true });
      try {
        const { data } = await SocialCommentsAPI.unlike(id);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ unliking: false });
      }
    },

    async handle(id) {
      this.setUIFlag({ handling: true });
      try {
        const { data } = await SocialCommentsAPI.handle(id);
        const comment = camelizeComment(data);
        this.upsertRecord(comment);
        return comment;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ handling: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await SocialCommentsAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },
  }),
});
